import { TrendingUp, Zap, Battery, Compass, type LucideIcon } from 'lucide-react';
import { HpScorePanel } from './HpScorePanel';
import type { FreeResult } from '../../lib/apiClient';

/**
 * The Bottleneck report's hero, copied from production's `report.tsx`.
 *
 * Production opens the paid report with the customer's High Performer Type, a
 * composite HP Performance Score with its letter grade, and four score cards —
 * each carrying a sentence explaining what the number means and which direction
 * is good. V2 showed four bare numbers in unlabelled boxes.
 *
 * **The direction matters and production states it.** A high Bottleneck Score
 * is bad and a high Execution Readiness is good; four numbers with no legend
 * invite exactly the wrong reading.
 */
/** Production's four cards, in production's order, with its own descriptions. */
const SCORE_CARDS: Array<{
  key: 'bottleneckScore' | 'executionReadinessScore' | 'recoveryDeficitScore' | 'clarityScore';
  label: string;
  icon: LucideIcon;
  description: string;
  /**
   * Production's own legend, derived there from a `direction` flag:
   * `higher-worse` reads "Higher = more active", `higher-better` reads
   * "Higher = stronger readiness".
   */
  legend: string;
}> = [
  {
    key: 'bottleneckScore',
    label: 'Bottleneck Score',
    icon: TrendingUp,
    description:
      'How strongly your top performance patterns are showing up right now. Higher means more active friction pulling against your output.',
    legend: 'Higher = more active',
  },
  {
    key: 'executionReadinessScore',
    label: 'Execution Readiness',
    icon: Zap,
    description:
      'How ready your current state is for focused, high-quality work. Lower means more internal barriers between you and actually doing the work.',
    legend: 'Higher = stronger readiness',
  },
  {
    key: 'recoveryDeficitScore',
    label: 'Recovery Gap',
    icon: Battery,
    description:
      'How depleted your system currently is. Higher means you are running on less reserve than sustained performance requires.',
    legend: 'Higher = more active',
  },
  {
    key: 'clarityScore',
    label: 'Clarity Score',
    icon: Compass,
    description:
      'How clear you are on direction and purpose. Lower means more fog or identity pressure is influencing your decisions.',
    legend: 'Higher = stronger readiness',
  },
];

export function ReportHero({
  result,
  firstName,
  whatThisMeans,
}: {
  result: FreeResult;
  /** Production personalises the eyebrow when it knows the customer's name. */
  firstName: string | null;
  whatThisMeans: string;
}) {
  return (
    <section
      className="overflow-hidden rounded-2xl border border-border bg-card"
      data-testid="report-hero"
    >
      <div className="flex flex-col gap-6 p-8 md:p-10">
        <div>
          <span
            className="mb-4 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary"
            data-testid="report-eyebrow"
          >
            {firstName ? `${firstName}'s High Performer Type` : 'Your High Performer Type'}
          </span>
          <h1 className="mb-1 text-3xl font-bold md:text-4xl">Bottleneck Assessment</h1>
          <p className="mb-3 text-lg font-semibold text-muted-foreground md:text-xl">
            {result.primaryType}
          </p>
          <p className="max-w-2xl leading-relaxed text-muted-foreground">{whatThisMeans}</p>
        </div>

        <HpScorePanel result={result} />

        <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
          {SCORE_CARDS.map((card) => {
            const value = result[card.key];
            if (typeof value !== 'number') return null;
            const Icon = card.icon;
            return (
              <div
                key={card.key}
                className="flex flex-col gap-2 rounded-xl border border-border bg-background p-5"
                data-testid="report-score-card"
                data-score={card.key}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                    {card.label}
                  </span>
                  <span className="text-2xl font-bold tabular-nums text-primary">{value}</span>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">{card.description}</p>
                <p className="text-xs italic text-muted-foreground/60">{card.legend}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
